import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Seo from "@/components/Seo";
import { HandHeart, ShieldAlert, AlertTriangle, Phone, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const CONCERN_LABEL = {
  immediate_safety: "Immediate safety / abuse",
  neglect_isolation: "Neglect or isolation",
  medical_mobility: "Medical or mobility need",
  financial_concern: "Financial concern / exploitation",
  food_utilities: "Food or utilities",
  housing: "Housing",
  spiritual_loneliness: "Spiritual loneliness",
  transportation: "Transportation",
};

const URGENCY = [
  { key: "immediate", label: "Immediate", body: "Welfare check needed this week — safety or essentials at risk." },
  { key: "this_week", label: "This week", body: "Concern needs follow-up within a few days." },
  { key: "ongoing", label: "Ongoing", body: "A long-term need — friendship, weekly visits, or recurring help." },
];

export default function LoveThyElder() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [meta, setMeta] = useState({ concern_types: [], urgency: [] });

  const [elderName, setElderName] = useState("");
  const [elderAge, setElderAge] = useState("");
  const [relationship, setRelationship] = useState("");
  const [elderCity, setElderCity] = useState("");
  const [elderState, setElderState] = useState("");
  const [elderContact, setElderContact] = useState("");
  const [concernTypes, setConcernTypes] = useState([]);
  const [urgency, setUrgency] = useState("this_week");
  const [description, setDescription] = useState("");
  const [canContact, setCanContact] = useState(true);
  const [reporterPhone, setReporterPhone] = useState("");

  useEffect(() => {
    api.get("/elder-reports/meta")
      .then(({ data }) => setMeta(data))
      .catch(() => {});
  }, []);

  const toggleConcern = (key) =>
    setConcernTypes((s) => (s.includes(key) ? s.filter((x) => x !== key) : [...s, key]));

  const submit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error("Please sign in to file a report — keeps the family accountable.");
      navigate("/login");
      return;
    }
    if (description.trim().length < 20) {
      toast.error("Please share at least a few sentences so we can act.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/elder-reports", {
        elder_name: elderName.trim(),
        elder_age: elderAge ? parseInt(elderAge, 10) : null,
        relationship: relationship.trim(),
        elder_city: elderCity.trim(),
        elder_state: elderState.trim(),
        elder_contact: elderContact.trim(),
        concern_types: concernTypes,
        urgency,
        description: description.trim(),
        reporter_can_be_contacted: canContact,
        reporter_phone: reporterPhone.trim(),
      });
      setSubmitted(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not submit the report. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-xl mx-auto px-6 py-24 text-center">
        <Seo title="Report received · Love Thy Elder · Jesus Just Us" noindex />
        <div className="jj-card p-10 fade-up" data-testid="elder-report-thanks">
          <CheckCircle2 className="h-10 w-10 text-burgundy mx-auto" strokeWidth={1.4} />
          <h1 className="font-serif text-4xl text-jjblue mt-5">Received with care.</h1>
          <p className="mt-4 text-[var(--jj-text-secondary)] leading-relaxed">
            Thank you for advocating for someone the world too often overlooks. Our admin team has been
            notified privately. You may be contacted for additional details.
          </p>
          <p className="mt-3 text-sm text-[var(--jj-muted)]">
            For immediate danger, please call <a href="tel:911" className="text-burgundy font-medium">911</a> or
            Adult Protective Services at <a href="tel:18006771116" className="text-burgundy font-medium">1-800-677-1116</a>.
          </p>
          <Link to="/" className="btn-primary mt-7 inline-block" data-testid="elder-report-home">Back to home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-12">
      <Seo
        title="Love Thy Elder · Report a concern · Jesus Just Us"
        description="A private, admin-only channel to flag concerns about an elder in your life or community. Reports are confidential and reviewed by Jesus Just Us admins only."
        path="/love-thy-elder"
      />

      <Link to="/" className="inline-flex items-center gap-1 text-sm text-burgundy mb-6 hover:underline" data-testid="elder-report-back">
        <ArrowLeft className="h-4 w-4" /> Back home
      </Link>

      <div className="overline text-burgundy flex items-center gap-2">
        <HandHeart className="h-3.5 w-3.5" /> Love Thy Elder
      </div>
      <h1 className="font-serif text-5xl text-jjblue mt-3 leading-tight">Report a concern, privately.</h1>
      <p className="mt-5 text-[var(--jj-text-secondary)] leading-relaxed">
        If a grandparent, neighbor, fellow congregant, or any elder you know is alone, struggling, or
        being mistreated — tell us here. This form is <strong>seen only by Jesus Just Us admins</strong>.
        It is never visible to other members.
      </p>

      {/* 911 banner */}
      <div className="mt-6 rounded-2xl bg-burgundy text-cream p-5 flex items-start gap-3" data-testid="elder-report-911">
        <AlertTriangle className="h-6 w-6 shrink-0" strokeWidth={1.6} />
        <div>
          <div className="font-serif text-xl leading-tight">If they are in immediate danger, call 911.</div>
          <p className="text-sm text-cream/85 mt-2 leading-relaxed">
            You can also call Adult Protective Services 24/7 at{" "}
            <a href="tel:18006771116" className="underline font-medium">1-800-677-1116</a>. This form is for
            community follow-up — it is not an emergency service.
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="jj-card p-7 mt-8 space-y-6" data-testid="elder-report-form">
        {/* Elder identity */}
        <div>
          <div className="overline text-burgundy">About the elder</div>
          <div className="grid sm:grid-cols-2 gap-4 mt-3">
            <div className="sm:col-span-2">
              <label className="text-sm text-jjblue/80">Their name *</label>
              <input
                required minLength={1} maxLength={160}
                value={elderName} onChange={(e) => setElderName(e.target.value)}
                className="input-jj mt-1"
                placeholder="Mrs. Mae Robinson"
                data-testid="elder-name"
              />
            </div>
            <div>
              <label className="text-sm text-jjblue/80">Approximate age</label>
              <input
                type="number" min="40" max="130"
                value={elderAge} onChange={(e) => setElderAge(e.target.value)}
                className="input-jj mt-1" placeholder="78"
                data-testid="elder-age"
              />
            </div>
            <div>
              <label className="text-sm text-jjblue/80">Your relationship to them</label>
              <input
                maxLength={80} value={relationship} onChange={(e) => setRelationship(e.target.value)}
                className="input-jj mt-1" placeholder="grandmother, neighbor, church mother…"
                data-testid="elder-relationship"
              />
            </div>
            <div>
              <label className="text-sm text-jjblue/80">City</label>
              <input
                maxLength={120} value={elderCity} onChange={(e) => setElderCity(e.target.value)}
                className="input-jj mt-1" placeholder="Atlanta"
                data-testid="elder-city"
              />
            </div>
            <div>
              <label className="text-sm text-jjblue/80">State</label>
              <input
                maxLength={80} value={elderState} onChange={(e) => setElderState(e.target.value)}
                className="input-jj mt-1" placeholder="GA"
                data-testid="elder-state"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="text-sm text-jjblue/80">Phone, address, or how to reach them (optional)</label>
              <input
                maxLength={240} value={elderContact} onChange={(e) => setElderContact(e.target.value)}
                className="input-jj mt-1" placeholder="555-555-5555 · 123 Main St, Apt 4"
                data-testid="elder-contact"
              />
              <p className="text-[11px] text-[var(--jj-muted)] mt-1.5 flex items-center gap-1.5">
                <ShieldAlert className="h-3 w-3" /> This is only seen by admins. Never shared with members.
              </p>
            </div>
          </div>
        </div>

        {/* Concern types */}
        <div>
          <div className="overline text-burgundy">What's the concern? <span className="text-[var(--jj-muted)] normal-case font-normal">(pick all that apply)</span></div>
          <div className="grid sm:grid-cols-2 gap-2 mt-3">
            {(meta.concern_types || []).map((key) => {
              const on = concernTypes.includes(key);
              return (
                <button
                  key={key} type="button" onClick={() => toggleConcern(key)}
                  data-testid={`elder-concern-${key}`}
                  className={`text-left p-3 rounded-xl border transition-all ${
                    on
                      ? "bg-[var(--jj-burgundy)] text-cream border-[var(--jj-burgundy)]"
                      : "bg-cream-surface border-jj hover:border-[var(--jj-gold)]"
                  }`}
                >
                  <div className="text-sm font-medium">{CONCERN_LABEL[key] || key}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Urgency */}
        <div>
          <div className="overline text-burgundy">How urgent?</div>
          <div className="grid sm:grid-cols-3 gap-2 mt-3">
            {URGENCY.map((u) => {
              const on = urgency === u.key;
              return (
                <button
                  key={u.key} type="button" onClick={() => setUrgency(u.key)}
                  data-testid={`elder-urgency-${u.key}`}
                  className={`text-left p-4 rounded-xl border transition-all ${
                    on
                      ? "bg-[var(--jj-burgundy)] text-cream border-[var(--jj-burgundy)]"
                      : "bg-cream-surface border-jj hover:border-[var(--jj-gold)]"
                  }`}
                >
                  <div className="text-sm font-medium">{u.label}</div>
                  <div className={`text-[11px] mt-1 leading-snug ${on ? "text-cream/85" : "text-[var(--jj-muted)]"}`}>{u.body}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="text-sm text-jjblue/80">What's happening? *</label>
          <textarea
            required minLength={20} maxLength={4000} rows={6}
            value={description} onChange={(e) => setDescription(e.target.value)}
            className="input-jj mt-1"
            placeholder="Share what you've seen, heard, or what they've told you. Anything that helps us respond well — recent events, who else is involved, what's already been tried."
            data-testid="elder-description"
          />
          <p className="mt-1.5 text-[11px] text-[var(--jj-muted)]">{description.length}/4000 — Please be specific. Avoid speculation; share facts.</p>
        </div>

        {/* Reporter */}
        <div>
          <div className="overline text-burgundy">About you</div>
          <p className="text-xs text-[var(--jj-muted)] mt-1">
            Signed in as <span className="text-jjblue/85">{user?.name || user?.email || "guest"}</span>.
            Admins can see your name &amp; email to follow up.
          </p>
          <div className="mt-3 grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-jjblue/80">Your phone (optional)</label>
              <input
                maxLength={40} value={reporterPhone} onChange={(e) => setReporterPhone(e.target.value)}
                className="input-jj mt-1" placeholder="555-555-1212"
                data-testid="elder-reporter-phone"
              />
            </div>
            <label className="flex items-start gap-2 select-none cursor-pointer sm:mt-7" data-testid="elder-can-contact-row">
              <input
                type="checkbox" checked={canContact}
                onChange={(e) => setCanContact(e.target.checked)}
                className="mt-1 h-4 w-4 accent-burgundy"
                data-testid="elder-can-contact"
              />
              <span className="text-xs text-jjblue/85 leading-relaxed">It's okay for an admin to contact me about this concern.</span>
            </label>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 pt-2 flex-wrap">
          <p className="text-[11px] text-[var(--jj-muted)] inline-flex items-center gap-1.5">
            <Phone className="h-3 w-3" /> Immediate danger? Call <a href="tel:911" className="text-burgundy font-medium underline">911</a>.
          </p>
          <button disabled={busy} className="btn-primary inline-flex items-center gap-2" data-testid="elder-submit">
            <HandHeart className="h-4 w-4" strokeWidth={1.7} /> {busy ? "Sending…" : "Send to admin team"}
          </button>
        </div>
      </form>
    </div>
  );
}
